import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth, useCart } from "@/App";
import { ShoppingBag, Menu, X, User, ChevronDown } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const Navbar = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { user, logout } = useAuth();
  const { cart, setCartOpen } = useCart();
  const location = useLocation();

  const navLinks = [
    { name: "Piloter sa stratégie", path: "/piloter-strategie" },
    { name: "Fidéliser ses clients", path: "/fideliser-clients" },
    { name: "Développer ses ventes", path: "/developper-ventes" },
    { name: "Valoriser sa marque", path: "/boutique" },
    { name: "Podcast", path: "/podcast" },
  ];

  const isActive = (path) => location.pathname === path;

  const cartItemsCount = cart.items?.reduce((sum, item) => sum + item.quantity, 0) || 0;

  return (
    <nav className="sticky top-0 z-50 bg-[#FDFBF7]/95 backdrop-blur-md border-b border-stone-200" data-testid="navbar">
      <div className="container-custom">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center" data-testid="logo-link">
            <img 
              src="https://customer-assets.emergentagent.com/job_moreati-preview/artifacts/abz6ktff_Copie%20de%20nouveau%20logo%20moreati%20%281%29.png" 
              alt="MOREATI" 
              className="h-12 object-contain"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-5">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`nav-link text-sm whitespace-nowrap ${isActive(link.path) ? "active text-[#C65D47]" : ""}`}
                data-testid={`nav-${link.path.replace("/", "")}`}
              >
                {link.name}
              </Link>
            ))}
            <Link to="/contact" className={`nav-link text-sm whitespace-nowrap ${isActive("/contact") ? "active text-[#C65D47]" : ""}`} data-testid="nav-contact">
              Contact
            </Link>
          </div>

          {/* Right Actions */}
          <div className="flex items-center gap-4">
            {/* Cart Button */}
            <button
              onClick={() => setCartOpen(true)}
              className="relative p-2 rounded-full hover:bg-stone-100 transition-colors"
              data-testid="cart-button"
            >
              <ShoppingBag className="w-6 h-6 text-[#1D3557]" />
              {cartItemsCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-[#C65D47] text-white text-xs rounded-full flex items-center justify-center font-medium">
                  {cartItemsCount}
                </span>
              )}
            </button>

            {/* User Menu */}
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger className="flex items-center gap-2 p-2 rounded-full hover:bg-stone-100 transition-colors" data-testid="user-menu-trigger">
                  <div className="w-8 h-8 rounded-full bg-[#1D3557] flex items-center justify-center">
                    <span className="text-white text-sm font-medium">
                      {user.first_name?.charAt(0)}{user.last_name?.charAt(0)}
                    </span>
                  </div>
                  <ChevronDown className="w-4 h-4 text-[#1D3557] hidden sm:block" />
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56 bg-white">
                  <div className="px-3 py-2">
                    <p className="font-medium text-[#1D3557]">{user.first_name} {user.last_name}</p>
                    <p className="text-sm text-stone-500">{user.email}</p>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to="/profile" className="cursor-pointer" data-testid="profile-link">
                      Mon Profil
                    </Link>
                  </DropdownMenuItem>
                  {user.is_admin && (
                    <DropdownMenuItem asChild>
                      <Link to="/admin" className="cursor-pointer" data-testid="admin-link">
                        Administration
                      </Link>
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={logout} className="text-[#E63946] cursor-pointer" data-testid="logout-button">
                    Déconnexion
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Link
                to="/login"
                className="flex items-center gap-2 px-4 py-2 rounded-full bg-[#1D3557] text-white hover:bg-[#2A4A6D] transition-colors"
                data-testid="login-button"
              >
                <User className="w-4 h-4" />
                <span className="hidden sm:inline">Connexion</span>
              </Link>
            )}

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="lg:hidden p-2 rounded-lg hover:bg-stone-100 transition-colors"
              data-testid="mobile-menu-button"
            >
              {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        className={`lg:hidden fixed inset-x-0 top-20 bg-white border-b border-stone-200 shadow-lg transform transition-all duration-300 ${
          mobileOpen ? "translate-y-0 opacity-100" : "-translate-y-full opacity-0 pointer-events-none"
        }`}
        data-testid="mobile-menu"
      >
        <div className="container-custom py-6 space-y-4">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              onClick={() => setMobileOpen(false)}
              className={`block py-3 px-4 rounded-lg text-lg font-medium transition-colors ${
                isActive(link.path)
                  ? "bg-[#C65D47]/10 text-[#C65D47]"
                  : "text-[#1D3557] hover:bg-stone-50"
              }`}
            >
              {link.name}
            </Link>
          ))}
          <Link
            to="/contact"
            onClick={() => setMobileOpen(false)}
            className={`block py-3 px-4 rounded-lg text-lg font-medium transition-colors ${
              isActive("/contact")
                ? "bg-[#C65D47]/10 text-[#C65D47]"
                : "text-[#1D3557] hover:bg-stone-50"
            }`}
          >
            Contact
          </Link>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
